import { useMutation } from "@tanstack/react-query"
import { useMethods, useToggle } from "react-use"
import { forwardRef, useImperativeHandle } from "react"
import { toast } from "react-toastify"

import { Button, Input, Modal, Text, Textarea } from "ui-kit/components-branded/index"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { validateEmail } from "utils/helpers"
import style from "modules/PsyCatalog/browser-side/ManualSelection/popup.module.scss"
import { sendManualSelectionRequest } from "modules/PsyCatalog/browser-side/ManualSelection/api"
import { BannerStorage } from "modules/PsyCatalog/browser-side/ManualSelection/BannerStorage"
import { UserAccount } from "utils/UserAccount"

type TextFieldKey = "message" | "email" | "name"

interface FormError {
    field: TextFieldKey
    message: string
}

interface State {
    message: string
    email: string
    name: string
    errors: FormError[]
    status: "idle" | "invalid" | "valid"
}

const initialState: State = {
    message: "",
    email: UserAccount.email ?? "",
    name: UserAccount.name ?? "",
    errors: [],
    status: "idle",
}

const errorTypes = {
    message: {
        empty: "Укажите ваш запрос",
    },
    email: {
        empty: "Укажите вашу почту",
        format: "Проверьте правильность написания почты.",
    },
    name: {
        empty: "Укажите ваше имя",
    },
}

const validations = {
    message: (value: string) => {
        if (!value.length) return errorTypes.message.empty
    },
    email: (value: string) => {
        if (!value.length) return errorTypes.email.empty
        if (!validateEmail(value)) return errorTypes.email.format
    },
    name: (value: string) => {
        if (!value.length) return errorTypes.name.empty
    },
}

function createMethods(state: State) {
    return {
        reset() {
            return initialState
        },
        changeField({ key, value }: { key: TextFieldKey; value: string }) {
            return { ...state, [key]: value }
        },
        validate({ key }: { key: TextFieldKey }) {
            const errors: FormError[] = state.errors.filter(({ field }) => field !== key)
            const value = state[key]
            const error = validations[key](value)
            if (error) {
                errors.push({ field: key, message: error })
            }
            return {
                ...state,
                errors,
                status: errors.length ? ("invalid" as const) : ("valid" as const),
            }
        },
    }
}

export interface PopupRef {
    show: () => void
}

export const Popup = forwardRef((_, ref) => {
    const [active, toggle] = useToggle(false)
    const [locked, toggleLocked] = useToggle(false)
    useImperativeHandle(
        ref,
        () => ({
            show: () => toggle(true),
        }),
        []
    )
    const [{ isVerified }] = useClientContext()
    const [state, methods] = useMethods(createMethods, initialState)
    const { mutate, isLoading } = useMutation({
        mutationFn: sendManualSelectionRequest,
        onSuccess: () => {
            toggle(false)
            toast.success("Запрос на подбор психолога отправлен")
            BannerStorage.getInstance().cleanStorage()
            toggleLocked(true)
            history.replaceState(null, "", location.href.replace(/[?&]manual=1/, ""))
        },
        onError: () => {
            toast.error("Произошла ошибка. Попробуйте еще раз.")
        },
    })
    function validateForm() {
        methods.validate({ key: "message" })
        methods.validate({ key: "email" })
        methods.validate({ key: "name" })
        return state.status
    }

    const errors = {
        email: state.errors.find((e) => e.field === "email"),
        name: state.errors.find((e) => e.field === "name"),
        message: state.errors.find((e) => e.field === "message"),
    }
    return (
        <Modal isActive={active}>
            <Modal.Content
                isHalf
                padding="M"
                isFullHeightMobile
                data-testid="psycatalog-request-on-psy-modal"
            >
                <Text isAccent isBlock>
                    Запросите подбор психологов.
                </Text>
                <Text>Опишите кратко, что вас беспокоит.</Text>
                <Input
                    className="mgt-3"
                    placeholder="Ваша почта"
                    disabled={(isVerified && !!UserAccount.email) || locked}
                    value={state.email}
                    onInput={(event) => {
                        const target = event.target as HTMLInputElement
                        methods.changeField({
                            key: "email",
                            value: target.value.replace(/\s+/g, ""),
                        })
                    }}
                    isDanger={!!errors.email}
                    onBlur={() => {
                        methods.validate({ key: "email" })
                    }}
                />
                {errors.email && <Text color="danger">{errors.email.message}</Text>}
                <Input
                    className="mgt-3"
                    placeholder="Ваше имя"
                    disabled={(isVerified && !!UserAccount.name) || locked}
                    value={state.name}
                    onInput={(event) => {
                        const target = event.target as HTMLInputElement
                        methods.changeField({ key: "name", value: target.value })
                    }}
                    onFocus={(event) => {
                        const target = event.target as HTMLInputElement
                        methods.changeField({ key: "name", value: target.value.trim() })
                    }}
                    isDanger={!!errors.name}
                    onBlur={() => {
                        methods.validate({ key: "name" })
                    }}
                />
                {errors.name?.message && <Text color="danger">{errors.name.message}</Text>}
                <Textarea
                    className="is-flex-grow-1 mgt-3"
                    placeholder="Сформулируйте ваш запрос"
                    value={state.message}
                    onChange={(event) => {
                        methods.changeField({ key: "message", value: event.target.value })
                    }}
                    color={errors.message ? "danger" : undefined}
                    onBlur={() => {
                        methods.validate({ key: "message" })
                    }}
                    disabled={locked}
                />
                {errors.message?.message && <Text color="danger">{errors.message.message}</Text>}
                <div className="is-flex mgt-3">
                    <Button
                        isFullwidth
                        className={style.button}
                        onClick={() => {
                            !locked && methods.reset()
                            toggle(false)
                        }}
                    >
                        Отменить
                    </Button>
                    <Button
                        color="primary"
                        isFullwidth
                        className={style.button}
                        onClick={() => {
                            validateForm() === "valid" &&
                                mutate({
                                    message: state.message,
                                    name: state.name,
                                    email: state.email,
                                })
                        }}
                        isLoading={isLoading}
                        disabled={locked}
                    >
                        Запросить
                    </Button>
                </div>
                <Text isSecondary className={style.personalInfo}>
                    Нажимая «Запросить», вы&nbsp;соглашаетесь с&nbsp;
                    <a
                        href={UserAccount.makePersonalAgreementUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.personalInfoLink}
                    >
                        условиями обработки персональных данных
                    </a>
                    .
                </Text>
            </Modal.Content>
        </Modal>
    )
})

Popup.displayName = "Popup"
